import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Center, Loader, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import {
  ANCHOR_HIGHLIGHT_EVENT,
  ANCHOR_PERSIST_EVENT,
  consumePendingAnchorHighlight,
  getPersistentAnchorHighlight,
} from '@/lib/anchorHighlight';
import type { BlockModel, DocxModel } from '@/lib/ooxml';
import { vnextApiClient } from '@/services/vnext-api';
import { DocxBlockList } from './DocxBlockList';
import { cachedDocx, cacheDocx, type ParsedDocx } from './docxCache';
import type { DocxEditBinding } from './editing';
import classes from './DocxContent.module.css';

const COVERAGE_FLOOR_PCT = 98;

const NO_IMAGES: Record<string, string> = {};

const IMAGE_MIME: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  svg: 'image/svg+xml',
  tif: 'image/tiff',
  tiff: 'image/tiff',
};

type DocxContentProps = (
  | { reportId: string; docxUrl?: never }
  | { docxUrl: string; reportId?: never }
) & {
  /** Text editing (the workbench's Document view); absent, read-only. */
  edit?: DocxEditBinding;
  /** Names the file's content, which never changes under this key (a
   * draft's file, a report version): the parsed document is kept and
   * opening it again doesn't download or parse it again (`docxCache`). */
  cacheKey?: string;
  /** A version of the document was read and its blocks rendered — what a
   * caller marking blocks from outside waits for. */
  onRendered?: (model: DocxModel) => void;
  /** What a caller adds at a block's end — a draft paragraph's citation
   * chips, say. A new function re-renders the blocks: keep it stable. */
  decorate?: (block: BlockModel) => ReactNode;
};

async function buildImageUrls(model: DocxModel): Promise<Record<string, string>> {
  const used = new Set<string>();
  for (const block of model.blocks) {
    for (const seg of block.segments) {
      if (seg.type === 'image') {
        used.add(seg.rId);
      }
    }
  }
  const urls: Record<string, string> = {};
  for (const rId of used) {
    const path = model.mediaTargets[rId];
    if (!path) {
      continue;
    }
    const bytes = await model.readPart(path);
    if (!bytes) {
      continue;
    }
    const ext = path.split('.').pop()?.toLowerCase() ?? '';
    const type = IMAGE_MIME[ext];
    if (!type) {
      continue; // EMF/WMF and friends: the Presentation tab renders those.
    }
    urls[rId] = URL.createObjectURL(new Blob([bytes as BlobPart], { type }));
  }
  return urls;
}

/**
 * Content-layer report view: the docx parsed on demand into normalized,
 * selectable typography. Every block carries `data-para-id` — the anchor
 * substrate for feedback selections and scope-component mapping. Page
 * fidelity questions belong to the Presentation (PDF) tab, permanently.
 */
export function DocxContent({
  reportId,
  docxUrl,
  edit,
  cacheKey,
  onRendered,
  decorate,
}: Readonly<DocxContentProps>) {
  const [parsed, setParsed] = useState<ParsedDocx | null>(() =>
    cacheKey ? (cachedDocx(cacheKey) ?? null) : null
  );
  const model = parsed?.model ?? null;
  const [error, setError] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const onLoadRef = useRef(edit?.onLoad);
  const onRenderedRef = useRef(onRendered);
  const editRef = useRef(edit);

  // The latest callbacks, without re-running the effect below for them.
  useEffect(() => {
    onLoadRef.current = edit?.onLoad;
    onRenderedRef.current = onRendered;
  });
  // Before any event can reach a block: its handlers call the latest binding.
  useLayoutEffect(() => {
    editRef.current = edit;
  });

  // The binding as the blocks see it: a new object only when what they show
  // changes (the edits waiting, the paragraph being edited), so the document
  // doesn't re-render whenever the view around it does.
  const editing = edit !== undefined;
  const pending = edit?.pending;
  const active = edit?.active ?? null;
  const blockEdit = useMemo<DocxEditBinding | undefined>(
    () =>
      editing && pending
        ? {
            pending,
            active,
            onActivate: (target) => editRef.current?.onActivate(target),
            onChange: (target, text) => editRef.current?.onChange(target, text),
            onClose: () => editRef.current?.onClose(),
          }
        : undefined,
    [editing, pending, active]
  );

  useEffect(() => {
    if (model) {
      onLoadRef.current?.(model);
      onRenderedRef.current?.(model);
    }
  }, [model]);

  // Feedback-anchor highlighting: consume asks from the AI QA panel. The
  // post-load consume covers asks fired before this view existed (the tab
  // flip mounts it, keepMounted=false); the listener covers live asks.
  useEffect(() => {
    if (!model) {
      return;
    }
    const applyHighlight = () => {
      const ids = consumePendingAnchorHighlight();
      const root = wrapperRef.current;
      if (!ids || !root) {
        return;
      }
      const blocks = ids
        .map((id) => root.querySelector(`[data-para-id="${CSS.escape(id)}"]`))
        .filter((el): el is HTMLElement => el instanceof HTMLElement);
      if (blocks.length === 0) {
        return;
      }
      blocks[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
      blocks.forEach((el) => el.classList.add(classes.anchorFlash));
      window.setTimeout(() => {
        blocks.forEach((el) => el.classList.remove(classes.anchorFlash));
      }, 2600);
    };
    applyHighlight();
    window.addEventListener(ANCHOR_HIGHLIGHT_EVENT, applyHighlight);
    return () => window.removeEventListener(ANCHOR_HIGHLIGHT_EVENT, applyHighlight);
  }, [model]);

  // Compose-time persistent marker: while feedback context is captured,
  // its blocks stay tinted so it's clear what the message is about.
  useEffect(() => {
    if (!model) {
      return;
    }
    const sync = () => {
      const root = wrapperRef.current;
      if (!root) {
        return;
      }
      const ids = new Set(getPersistentAnchorHighlight());
      root.querySelectorAll<HTMLElement>('[data-para-id]').forEach((el) => {
        el.classList.toggle(classes.anchorPersist, ids.has(el.dataset.paraId ?? ''));
      });
    };
    sync();
    window.addEventListener(ANCHOR_PERSIST_EVENT, sync);
    return () => window.removeEventListener(ANCHOR_PERSIST_EVENT, sync);
  }, [model]);

  useEffect(() => {
    const hit = cacheKey ? cachedDocx(cacheKey) : undefined;
    if (hit) {
      setParsed(hit);
      return undefined;
    }
    let cancelled = false;
    // Image URLs this mount made and must release (a cached document's
    // belong to the cache).
    let owned: Record<string, string> = {};

    async function load() {
      try {
        const url = docxUrl ?? (await vnextApiClient.downloadReportDocx(reportId!)).url;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch document (${response.status})`);
        }
        const bytes = await response.arrayBuffer();
        if (cancelled) {
          return;
        }
        const { parseDocx } = await import('@/lib/ooxml');
        const read = await parseDocx(bytes);
        const urls = await buildImageUrls(read);
        if (cancelled) {
          Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
          return;
        }
        const next = { model: read, imageUrls: urls };
        if (cacheKey) {
          cacheDocx(cacheKey, next);
        } else {
          owned = urls;
        }
        setParsed(next);

        if (read.coverage.pct < COVERAGE_FLOOR_PCT) {
          Sentry.captureMessage('docx content coverage below floor', {
            level: 'warning',
            extra: {
              reportId,
              coveragePct: read.coverage.pct,
              unknownBlockTags: read.coverage.unknownBlockTags,
              unknownInlineTags: read.coverage.unknownInlineTags,
            },
          });
        }
      } catch (err) {
        if (!cancelled) {
          Sentry.captureException(err);
          setError(err instanceof Error ? err.message : 'Failed to load document');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      Object.values(owned).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [reportId, docxUrl, cacheKey]);

  if (error) {
    return (
      <Center py="xl">
        <Text c="red" size="sm">
          {error}
        </Text>
      </Center>
    );
  }
  if (!model) {
    return (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    );
  }

  return (
    <div className={classes.wrapper} ref={wrapperRef}>
      {model.coverage.pct < COVERAGE_FLOOR_PCT && (
        <Text size="xs" c="dimmed" className={classes.coverageNote}>
          Content view covers {model.coverage.pct.toFixed(1)}% of this document's text — use the
          Presentation tab for the complete rendering.
        </Text>
      )}
      <DocxBlockList
        model={model}
        imageUrls={parsed?.imageUrls ?? NO_IMAGES}
        edit={blockEdit}
        decorate={decorate}
      />
    </div>
  );
}
